/**
 * ReportingProfilesPage — Phase 4 RXF
 *
 * Create and manage personal reporting profiles (named style presets).
 * Profiles can be activated per-exam or set as a default.
 * Org defaults set by admin appear at the top as read-only references.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PageHeaderNavigation from '../components/common/PageHeaderNavigation';
import { useAppSelector } from '../hooks/reduxHooks';
import { selectReportingPrefs } from '../store/userPreferencesSlice';
import {
  listReportingProfiles,
  createReportingProfile,
  updateReportingProfile,
  updateOrgReportingProfile,
  activateReportingProfile,
  deleteReportingProfile,
  type ReportingProfile,
  type ReportingProfileRequest,
} from '../services/rxfService';

const DEFAULTS: ReportingProfileRequest = {
  name: '',
  description: '',
  report_length: 'detailed',
  impression_style: 'narrative',
  rec_style: 'standard',
  bold_positives: true,
  max_impression_points: 0,
  custom_instructions: '',
  target_modalities: [],
};

const MODALITY_OPTIONS = ['CT', 'MRI', 'US', 'XR', 'NM', 'PET', 'Mammo', 'Fluoro'];

const ProfileDialog: React.FC<{
  open: boolean;
  initial?: ReportingProfile;
  onClose: () => void;
  onSave: (data: ReportingProfileRequest) => void;
  saving: boolean;
}> = ({ open, initial, onClose, onSave, saving }) => {
  const [form, setForm] = useState<ReportingProfileRequest>(DEFAULTS);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name,
              description: initial.description || '',
              report_length: initial.report_length || 'detailed',
              impression_style: initial.impression_style || 'numbered_synthesis',
              rec_style: initial.rec_style || 'standard',
              bold_positives: initial.bold_positives || false,
              max_impression_points: initial.max_impression_points || 0,
              custom_instructions: initial.custom_instructions || '',
              target_modalities: initial.target_modalities || [],
            }
          : DEFAULTS,
      );
    }
  }, [open, initial]);

  const set = (key: keyof ReportingProfileRequest, val: unknown) =>
    setForm((f) => ({ ...f, [key]: val }));

  const toggleModality = (m: string) =>
    set(
      'target_modalities',
      (form.target_modalities || []).includes(m)
        ? (form.target_modalities || []).filter((x) => x !== m)
        : [...(form.target_modalities || []), m],
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit Profile' : 'New Reporting Profile'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Profile Name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Description (optional)"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
          />

          <Divider />

          <FormControl size="small" fullWidth>
            <InputLabel>Report Length</InputLabel>
            <Select
              value={form.report_length}
              label="Report Length"
              onChange={(e) => set('report_length', e.target.value)}
            >
              <MenuItem value="concise">Concise</MenuItem>
              <MenuItem value="detailed">Detailed (default)</MenuItem>
              <MenuItem value="comprehensive">Comprehensive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Impression Style</InputLabel>
            <Select
              value={form.impression_style}
              label="Impression Style"
              onChange={(e) => set('impression_style', e.target.value)}
            >
              <MenuItem value="numbered_synthesis">Numbered Synthesis (default)</MenuItem>
              <MenuItem value="brief_bullets">Brief Bullets</MenuItem>
              <MenuItem value="narrative">Narrative Prose</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Recommendations</InputLabel>
            <Select
              value={form.rec_style}
              label="Recommendations"
              onChange={(e) => set('rec_style', e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="standard">Standard (default)</MenuItem>
              <MenuItem value="aggressive">Include All</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={form.bold_positives}
                onChange={(e) => set('bold_positives', e.target.checked)}
                size="small"
              />
            }
            label="Bold positive findings"
          />

          <Box>
            <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
              Max impression points (0 = no limit)
            </Typography>
            <Slider
              value={form.max_impression_points ?? 0}
              onChange={(_, v) => set('max_impression_points', v as number)}
              min={0}
              max={10}
              step={1}
              marks
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>

          <TextField
            label="Custom instructions (optional)"
            value={form.custom_instructions}
            onChange={(e) => set('custom_instructions', e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="E.g. Always include a dedicated renal cyst characterisation paragraph"
          />

          <Box>
            <Typography variant="caption" sx={{ mb: 0.75, display: 'block' }}>
              Target modalities (leave empty = apply to all)
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {MODALITY_OPTIONS.map((m) => (
                <Chip
                  key={m}
                  label={m}
                  size="small"
                  onClick={() => toggleModality(m)}
                  color={(form.target_modalities || []).includes(m) ? 'primary' : 'default'}
                  variant={(form.target_modalities || []).includes(m) ? 'filled' : 'outlined'}
                  clickable
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={saving || !form.name.trim()}
        >
          {saving ? <CircularProgress size={16} /> : initial ? 'Save Changes' : 'Create Profile'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProfileCard: React.FC<{
  profile: ReportingProfile;
  isActive: boolean;
  isAdmin: boolean;
  onActivate: () => void;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ profile, isActive, isAdmin, onActivate, onEdit, onDelete }) => (
  <Card
    variant="outlined"
    sx={{
      borderColor: isActive ? 'primary.main' : undefined,
      borderWidth: isActive ? 2 : 1,
    }}
  >
    <CardContent sx={{ pb: '12px !important' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {profile.is_org_default ? (
              <BusinessIcon fontSize="small" color="action" />
            ) : (
              <PersonIcon fontSize="small" color="action" />
            )}
            <Typography variant="subtitle2">{profile.name}</Typography>
            {isActive && (
              <Chip size="small" icon={<CheckCircleIcon />} label="Active" color="primary" />
            )}
            {profile.is_org_default && (
              <Chip size="small" label="Org Default" variant="outlined" />
            )}
          </Stack>
          {profile.description && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {profile.description}
            </Typography>
          )}
        </Box>
        {(isAdmin || !profile.is_org_default) && (
          <Stack direction="row" spacing={0}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!profile.is_org_default && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
        <Chip label={profile.report_length || 'detailed'} size="small" variant="outlined" />
        <Chip label={profile.impression_style || 'numbered'} size="small" variant="outlined" />
        {profile.bold_positives && <Chip label="Bold +" size="small" variant="outlined" color="warning" />}
        {(profile.max_impression_points ?? 0) > 0 && (
          <Chip label={`≤${profile.max_impression_points} imp.`} size="small" variant="outlined" />
        )}
        {(profile.target_modalities || []).map((m) => (
          <Chip key={m} label={m} size="small" variant="outlined" color="secondary" />
        ))}
      </Stack>

      {!isActive && (
        <Button size="small" sx={{ mt: 1 }} onClick={onActivate}>
          Activate
        </Button>
      )}
    </CardContent>
  </Card>
);

const ReportingProfilesPage: React.FC = () => {
  const reportingPrefs = useAppSelector(selectReportingPrefs);
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = user?.role === 'admin';
  const [profiles, setProfiles] = useState<ReportingProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ReportingProfile | undefined>();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      setProfiles(await listReportingProfiles());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleSave = async (data: ReportingProfileRequest) => {
    setSaving(true);
    try {
      if (editTarget) {
        if (editTarget.is_org_default) {
          await updateOrgReportingProfile(editTarget.id, data);
        } else {
          await updateReportingProfile(editTarget.id, data);
        }
        setToast({ msg: 'Profile updated', sev: 'success' });
      } else {
        await createReportingProfile(data);
        setToast({ msg: 'Profile created', sev: 'success' });
      }
      setDialogOpen(false);
      await reload();
    } catch {
      setToast({ msg: 'Failed to save profile', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await activateReportingProfile(id);
      setToast({ msg: 'Profile activated — will apply to your next report', sev: 'success' });
      await reload();
    } catch {
      setToast({ msg: 'Failed to activate profile', sev: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this profile?')) return;
    try {
      await deleteReportingProfile(id);
      await reload();
    } catch {
      setToast({ msg: 'Failed to delete profile', sev: 'error' });
    }
  };

  const orgProfiles = profiles.filter((p) => p.is_org_default);
  const personalProfiles = profiles.filter((p) => !p.is_org_default);

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <PageHeaderNavigation
        title="Reporting Profiles"
        subtitle="Named style presets — activate one to shape every report you generate"
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditTarget(undefined); setDialogOpen(true); }}
        >
          New Profile
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {orgProfiles.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
                Organisation Defaults
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {orgProfiles.map((p) => (
                  <Grid item xs={12} sm={6} key={p.id}>
                    <ProfileCard
                      profile={p}
                      isActive={reportingPrefs.activeProfileId === p.id}
                      isAdmin={isAdmin}
                      onActivate={() => handleActivate(p.id)}
                      onEdit={() => { setEditTarget(p); setDialogOpen(true); }}
                      onDelete={() => handleDelete(p.id)}
                    />
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mt: 3 }} />
            </Box>
          )}

          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            My Profiles
          </Typography>
          {personalProfiles.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No personal profiles yet — create one to get started
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {personalProfiles.map((p) => (
                <Grid item xs={12} sm={6} key={p.id}>
                  <ProfileCard
                    profile={p}
                    isActive={reportingPrefs.activeProfileId === p.id}
                    isAdmin={isAdmin}
                    onActivate={() => handleActivate(p.id)}
                    onEdit={() => { setEditTarget(p); setDialogOpen(true); }}
                    onDelete={() => handleDelete(p.id)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      <ProfileDialog
        open={dialogOpen}
        initial={editTarget}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        saving={saving}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.sev ?? 'info'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportingProfilesPage;
